import React from 'react';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import store, { persistor } from './store';
import Theme from 'main/Theme';
import Layout from 'components/layout';
import history from './history';
//import mockServer from './mock';
import appConfig from 'configs/app.config';
import './locales';
import './App.css';
import './standalone-overrides.css';
import SessionKeeper from 'views/auth/SignIn/SessionKeeper';
import User from 'main/user/User';
import AppInitializer from './components/common/AppInitializer';
import ElectronBridge from './components/common/ElectronBridge';
import ChunkErrorBoundary from './components/common/ChunkErrorBoundary';
import MaintenanceBanner from './components/common/MaintenanceBanner';
import { previewIndexedDBData, clearIndexedDBKey, clearBaseDataOnly } from 'utils/indexedDBPreview';
import { Dialog, Button } from 'components/ui';
import { PURGE } from 'redux-persist';
import { getConfig } from '@/configs/edition.config';
import { useInviteHandler } from 'utils/hooks/useInviteHandler';
import { applySessionCommUse } from 'services/commClient';
// dev 빌드에서만 window.__appTestHooks 노출 (QBase 테스트 센터/Playwright runner 용)
import 'utils/testHooks';

// QPM SDK는 RUM(rum.js)에서 통합 관리됨 — 현재 기능 전체 비활성 (lib/qpm-sdk/enabled.js)

// Standalone 모드에서는 HashRouter 사용 (file:// 프로토콜에서 BrowserRouter가 동작하지 않음)
const editionConfig = getConfig();
const Router = editionConfig.isStandalone ? HashRouter : BrowserRouter;

const environment = process.env.NODE_ENV;

if (environment !== 'production' && appConfig.enableMock) {
    //mockServer({ environment });
}

function AppContent() {
    // Redux에서 상태 구독
    const state = useSelector(state => state);
    const user = state?.auth?.user || {};
    const { userId } = user;
    const { apiListLoaded } = useSelector(state => state.base.common);

    // 이메일 초대 URL 처리 (로그인 상태가 되면 자동으로 공유그룹 참여 + 모델 열기)
    useInviteHandler();

    return (
        <>
            {/* ElectronBridge - Electron 파일 이벤트 처리 */}
            <ElectronBridge />

            {/* AppInitializer - 중앙화된 초기화 로직 */}
            <AppInitializer />

            {/* Theme과 Layout은 항상 렌더링 (로그인 화면 표시 위해) */}
            <Theme>
                {/* 큐베이스 점검(쓰기 동결) 안내 띠 — 점검 중에만 보인다 */}
                <MaintenanceBanner />
                {/* userId가 있고 초기화 완료된 경우에만 SessionKeeper, User 활성화 */}
                {userId && apiListLoaded && (
                    <>
                        <User />
                        <SessionKeeper />
                    </>
                )}
                <Layout />
            </Theme>
        </>
    );
}


// 세션 에러/로딩 화면은 React 트리 초기 단계라 useSelector 로 테마를 읽기 어렵다.
// 테마 시스템이 <html> 에 붙여 둔 `dark` 클래스를 직접 읽어 색을 고른다.
// (이 화면들이 흰색으로 고정되어 있어 다크모드에서 앱 시작마다 흰 화면이 번쩍였다.)
const bootTheme = () => {
    const dark = typeof document !== 'undefined'
        && document.documentElement.classList.contains('dark');
    return dark
        ? { surface: '#1f2937', text: '#f3f4f6', subText: '#9ca3af', danger: '#f87171' }
        : { surface: '#fff', text: '#111827', subText: '#666', danger: '#dc2626' };
};

function App() {
    // Standalone 모드 감지
    const editionConfig = React.useMemo(() => getConfig(), []);
    const isStandalone = editionConfig.isStandalone;

    // QPM SDK는 RUM(rum.js)에서 통합 관리됨
    // 상단 토글로 활성화/비활성화 가능 (기본: off)

    const [restorationDecision, setRestorationDecision] = React.useState(null); // null | 'restore' | 'skip'
    const [previewData, setPreviewData] = React.useState(null);
    const [showDialog, setShowDialog] = React.useState(false);
    const [isChecking, setIsChecking] = React.useState(!isStandalone); // Standalone은 즉시 false
    const [sessionChecked, setSessionChecked] = React.useState(isStandalone); // Standalone은 즉시 true
    const [isSessionValid, setIsSessionValid] = React.useState(isStandalone); // Standalone은 즉시 유효
    const [sessionError, setSessionError] = React.useState(null);

    // Standalone 모드 로깅
    React.useEffect(() => {
        if (isStandalone) {
            // Standalone은 IndexedDB 체크도 건너뛰고 바로 앱 시작
            setRestorationDecision('restore');
        }
    }, [isStandalone, editionConfig.edition]);

    // 1단계: 세션 체크 (가장 먼저 실행) - 타임아웃 5초 (Cloud 모드만)
    const checkSession = React.useCallback(async () => {
        // Standalone 모드는 세션 체크 건너뜀
        if (isStandalone) {
            return;
        }

        setSessionError(null);
        setSessionChecked(false);

        // 타임아웃을 위한 AbortController
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5초 타임아웃

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BE_API}/qbase/api/keep-session`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    signal: controller.signal
                }
            );

            clearTimeout(timeoutId);

            if (response.ok) {
                applySessionCommUse(await response.json());
                setIsSessionValid(true);
                setSessionError(null);
            } else if (response.status === 401 || response.status === 403) {
                setIsSessionValid(false);
                setSessionError(null);
            } else {
                console.error('⚠️ 세션 확인 응답 이상:', response.status);
                setSessionError(`세션 확인 실패 (상태 코드: ${response.status})`);
            }
        } catch (error) {
            clearTimeout(timeoutId);

            if (error.name === 'AbortError') {
                console.error('❌ 세션 체크 타임아웃 (5초)');
                setSessionError('세션 확인 시간 초과 (5초)\n서버 응답이 느립니다.');
            } else {
                console.error('❌ 세션 체크 실패:', error.message);
                setSessionError(`세션 확인 실패: ${error.message}`);
            }
        }
        setSessionChecked(true);
    }, [isStandalone]);

    React.useEffect(() => {
        if (!isStandalone) {
            checkSession();
        }
    }, [checkSession, isStandalone]);

    // 로그인 후 세션 상태 동기화 (로그인 성공 시 isSessionValid 업데이트)
    React.useEffect(() => {
        const unsubscribe = store.subscribe(() => {
            const state = store.getState();
            const signedIn = state.auth?.session?.signedIn;
            if (signedIn && sessionChecked && !isSessionValid) {
                setIsSessionValid(true);
            }
        });
        return unsubscribe;
    }, [sessionChecked, isSessionValid]);

    // 2단계: IndexedDB 체크 (세션이 유효한 경우에만)
    React.useEffect(() => {
        if (!sessionChecked || !isSessionValid) {
            return;
        }

        const checkData = async () => {
            const preview = await previewIndexedDBData();

            if (!preview || preview.modelCount === 0) {
                // 데이터 없음 → 정상 복원 진행 (auth는 복원되어야 함)
                setRestorationDecision('restore');
                setIsChecking(false);
                return;
            }

            setPreviewData(preview);

            if (preview.totalEntities >= 100) {
                // 대량 데이터 → 사용자에게 물어봄
                setShowDialog(true);
                setIsChecking(false);
            } else {
                // 소량 데이터 → 자동 복원
                setRestorationDecision('restore');
                setIsChecking(false);
            }
        };

        checkData();
    }, [sessionChecked, isSessionValid]);

    // 3단계: 세션 만료 시 persist 정리
    React.useEffect(() => {
        if (sessionChecked && !isSessionValid) {
            persistor.purge().then(() => {
            }).catch((error) => {
                console.error('❌ Redux persist 정리 실패:', error);
            });
        }
    }, [sessionChecked, isSessionValid]);

    // 복원하기 선택
    const handleRestore = () => {
        setRestorationDecision('restore');
        setShowDialog(false);
    };

    // 건너뛰기 선택
    const handleSkip = async () => {

        // IndexedDB에서 base.common.multiModelData만 제거 (auth/locale 유지)
        await clearBaseDataOnly();

        setRestorationDecision('skip');
        setShowDialog(false);
    };

    // 삭제하기 선택
    const handleDelete = async () => {

        // 1. Redux state 초기화
        await persistor.purge();

        // 2. IndexedDB에서 persist 데이터 완전 삭제
        await clearIndexedDBKey('persist:datamodeler');

        setRestorationDecision('skip');
        setShowDialog(false);
    };

    // 세션 에러가 발생하면 에러 다이얼로그 표시
    if (sessionError) {
        const t = bootTheme();
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999
            }}>
                <div style={{
                    backgroundColor: t.surface,
                    borderRadius: '8px',
                    padding: '32px',
                    maxWidth: '500px',
                    width: '90%',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                    <div style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        marginBottom: '16px',
                        color: t.danger
                    }}>
                        ⚠️ 세션 확인 오류
                    </div>
                    <div style={{
                        fontSize: '14px',
                        marginBottom: '24px',
                        color: t.subText,
                        whiteSpace: 'pre-line',
                        lineHeight: '1.6'
                    }}>
                        {sessionError}
                    </div>
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'flex-end'
                    }}>
                        <button
                            onClick={checkSession}
                            style={{
                                padding: '10px 24px',
                                backgroundColor: '#3498db',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#2980b9'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#3498db'}
                        >
                            재시도
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // 세션 체크 중이면 로딩 화면
    if (!sessionChecked) {
        const t = bootTheme();
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: t.surface,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999
            }}>
                <div style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '20px',
                    color: '#333'
                }}>
                    세션 확인 중...
                </div>
                <div style={{
                    width: '50px',
                    height: '50px',
                    border: '5px solid #f3f3f3',
                    borderTop: '5px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }} />
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // 세션이 유효하지 않으면 즉시 로그인 화면으로 이동
    if (sessionChecked && !isSessionValid) {

        // 최소한의 구조로 로그인 페이지만 렌더링 (AppContent, AppInitializer 없음)
        return (
            <Provider store={store}>
                <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    <Theme>
                        <Layout />
                    </Theme>
                </Router>
            </Provider>
        );
    }

    // IndexedDB 체크 중이면 로딩 화면
    if (isChecking) {
        const t = bootTheme();
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: t.surface,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999
            }}>
                <div style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '20px',
                    color: '#333'
                }}>
                    데이터 확인 중...
                </div>
                <div style={{
                    width: '50px',
                    height: '50px',
                    border: '5px solid #f3f3f3',
                    borderTop: '5px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }} />
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // 복원 확인 다이얼로그
    const restorationDialog = showDialog && previewData ? (
        <Dialog
            isOpen={showDialog}
            onClose={handleSkip}
            width={520}
            overlayClassName="dialog-overlay-high-priority"
        >
            <div style={{ padding: '28px 24px 20px' }}>
                <h3 style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    textAlign: 'center',
                    marginBottom: '6px',
                    color: '#1f2937',
                }}>
                    이전 작업이 발견되었습니다
                </h3>
                <p style={{
                    fontSize: '13px',
                    color: '#6b7280',
                    textAlign: 'center',
                    marginBottom: '20px',
                }}>
                    {previewData.modelCount}개 모델의 작업 데이터가 저장되어 있습니다.
                </p>

                {/* 카드형 선택 영역 */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                    {/* 무시 카드 */}
                    <div
                        onClick={handleSkip}
                        style={{
                            flex: 1,
                            border: '2px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '20px 16px',
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.2s',
                            background: '#fafafa',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#9ca3af';
                            e.currentTarget.style.background = '#f3f4f6';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#e5e7eb';
                            e.currentTarget.style.background = '#fafafa';
                        }}
                    >
                        <div style={{ fontSize: '28px', marginBottom: '8px' }}>🗑️</div>
                        <div style={{
                            fontSize: '15px',
                            fontWeight: 700,
                            color: '#6b7280',
                            marginBottom: '16px',
                        }}>무시</div>
                        <div style={{
                            fontSize: '12px',
                            color: '#9ca3af',
                            lineHeight: '1.6',
                        }}>
                            저장된 데이터를 삭제하고<br />새로 시작합니다
                        </div>
                    </div>

                    {/* 복원 카드 */}
                    <div
                        onClick={handleRestore}
                        style={{
                            flex: 1,
                            border: '2px solid #6366f1',
                            borderRadius: '12px',
                            padding: '20px 16px',
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.2s',
                            background: '#eef2ff',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#4f46e5';
                            e.currentTarget.style.background = '#e0e7ff';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#6366f1';
                            e.currentTarget.style.background = '#eef2ff';
                        }}
                    >
                        <div style={{ fontSize: '28px', marginBottom: '8px' }}>✨</div>
                        <div style={{
                            fontSize: '15px',
                            fontWeight: 700,
                            color: '#4f46e5',
                            marginBottom: '16px',
                        }}>복원</div>

                        {/* 엔터티 / 속성 수 크게 표시 */}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '12px' }}>
                            <div>
                                <div style={{ fontSize: '24px', fontWeight: 800, color: '#4f46e5' }}>
                                    {previewData.totalEntities}
                                </div>
                                <div style={{ fontSize: '11px', color: '#6b7280' }}>엔터티</div>
                            </div>
                            <div>
                                <div style={{ fontSize: '24px', fontWeight: 800, color: '#4f46e5' }}>
                                    {previewData.totalAttrs || 0}
                                </div>
                                <div style={{ fontSize: '11px', color: '#6b7280' }}>속성</div>
                            </div>
                        </div>

                        {/* undo/redo 히스토리 */}
                        {(previewData.undoCount > 0 || previewData.redoCount > 0) && (
                            <div style={{
                                fontSize: '11px',
                                color: '#818cf8',
                                borderTop: '1px solid #c7d2fe',
                                paddingTop: '8px',
                                marginTop: '4px',
                            }}>
                                Undo {previewData.undoCount}건 / Redo {previewData.redoCount}건
                            </div>
                        )}
                    </div>
                </div>

                {previewData.totalEntities >= 500 && (
                    <p style={{
                        fontSize: '12px',
                        color: '#d97706',
                        textAlign: 'center',
                        marginBottom: '8px',
                    }}>
                        ⚠️ 데이터가 많아 로딩에 시간이 걸릴 수 있습니다.
                    </p>
                )}
            </div>
        </Dialog>
    ) : null;

    // 결정이 내려진 후에만 앱 렌더링
    if (restorationDecision === null) {
        return restorationDialog;
    }

    return (
        <ChunkErrorBoundary>
            {restorationDialog}
            <Provider store={store}>
                <PersistGate
                    loading={null}
                    persistor={persistor}
                    // auth는 항상 복원, base만 선택적으로 처리
                    onBeforeLift={async () => {
                        // auth, locale는 항상 복원됨
                        // skip 선택 시 base의 multiModelData만 나중에 초기화
                    }}
                >
                    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                        <AppContent />
                    </Router>
                </PersistGate>
            </Provider>
        </ChunkErrorBoundary>
    );
}

export default App;